import { GitBranch } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { appVersion, RELEASE_NOTES_URL } from '@/lib/appVersion';
import { cn } from '@/lib/utils';

interface AppVersionBadgeProps {
  /** Свёрнутый сайдбар (72px) — показываем только короткий sha. */
  collapsed?: boolean;
  className?: string;
}

/**
 * AppVersionBadge (AK-274 + release-mgmt) — индикатор версии фронтенда.
 *
 * Видно на ЛЮБОЙ странице (рендерится в футере Sidebar, над user-dropdown).
 * Hover/tap → Tooltip с деталями (sha, branch, build-time, env).
 * Клик → открывает Confluence-летопись релизов в новой вкладке.
 *
 * Формат: `v1.1049.0 · prod` (env приглушённым цветом).
 * SHA — в title/tooltip.
 *
 * Источник данных — build-time `@/lib/appVersion` (Vite define). Без сети.
 */
export function AppVersionBadge({
  collapsed = false,
  className,
}: AppVersionBadgeProps) {
  const { sha, branch, buildTime, env, label } = appVersion;

  // Нормализованный env для отображения.
  const envTag = env === 'production' ? 'prod' : env;

  // В collapsed-режиме места нет на полный label → показываем версию (с 'v'),
  // без env-тега. (Founder: «лучше писать v», а не голый sha.)
  const visibleText = collapsed ? label.split(' · ')[0] : label;

  const buildTimeText =
    buildTime !== 'unknown'
      ? new Date(buildTime).toLocaleString('ru-RU', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : 'unknown';

  // Разбиваем label на версию и env для раздельной стилизации:
  // "v1.1049.0 · prod" → часть до " · " (основная) + " · prod" (приглушённая).
  const dotIndex = label.indexOf(' · ');
  const versionPart = dotIndex >= 0 ? label.slice(0, dotIndex) : label;
  const envPart = dotIndex >= 0 ? label.slice(dotIndex) : null;

  const handleClick = () => {
    window.open(RELEASE_NOTES_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Версия приложения: ${label}. Нажмите, чтобы открыть летопись релизов.`}
          title={sha !== 'unknown' ? `SHA: ${sha}` : undefined}
          onClick={handleClick}
          className={cn(
            'flex w-full items-center justify-center gap-1 px-2 py-1.5',
            'text-[10px] leading-tight text-muted-foreground',
            'hover:text-foreground transition-colors',
            'cursor-pointer truncate',
            className,
          )}
        >
          {collapsed ? (
            <span className="truncate font-mono">{visibleText}</span>
          ) : (
            <span className="truncate font-mono">
              <span>{versionPart}</span>
              {envPart && (
                <span className="text-muted-foreground/60">{envPart}</span>
              )}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" className="max-w-[280px]">
        <div className="space-y-1 text-left">
          <div className="font-mono font-semibold text-foreground">
            {label}
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-muted-foreground">
            <dt>sha</dt>
            <dd className="font-mono text-foreground">{sha}</dd>
            <dt className="flex items-center gap-1">
              <GitBranch className="h-3 w-3" />
              branch
            </dt>
            <dd className="truncate font-mono text-foreground">{branch}</dd>
            <dt>build</dt>
            <dd className="text-foreground">{buildTimeText}</dd>
            <dt>env</dt>
            <dd className="font-mono text-foreground">{envTag}</dd>
          </dl>
          <div className="pt-0.5 text-[9px] text-muted-foreground/60">
            Нажмите, чтобы открыть летопись релизов
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export default AppVersionBadge;
